import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PROPERTY_TYPES } from "@sakani/contracts";
import { api, ApiError, mediaUrl, shrinkImage, uploadImage } from "../api.ts";
import { useAuth, type Me } from "../auth.tsx";
import { AuthForm } from "../ui/AuthForm.tsx";
import { money } from "../format.ts";
import { pick, useI18n } from "../i18n.tsx";

type L10n = { fr: string; ar?: string; en?: string };
interface Mine { id: string; title: L10n; status: string; basePriceMinor: number; currency: string }
interface Stay { id: string; code: string; status: string; listingTitle: L10n | null; checkIn: string; checkOut: string; nights: number; currency: string; payout: { amountMinor: number; status: string } | null }
interface Full extends Mine { cityId: number; description: L10n; propertyType: string; address: string; lat: number | null; lng: number | null; maxGuests: number; bedrooms: number; beds: number; bathrooms: string; cleaningFeeMinor: number; photos: { id: string; key: string; position: number }[] }
interface Vocab { cities: { id: number; name: L10n }[] }
const toMinor = (s: string) => Math.round(Number(s.replace(",", ".")) * 100);

function useErr() {
  const { t } = useI18n();
  const [msg, setMsg] = useState<string | null>(null);
  return { msg, clear: () => setMsg(null), fail: (e: unknown) => { const k = e instanceof ApiError ? `err.${e.code}` : ""; setMsg(k && t(k) !== k ? t(k) : e instanceof ApiError && e.message ? e.message : t("error")); } };
}

function Apply({ me, done }: { me: Me; done: () => void }) {
  const { t, locale } = useI18n();
  const [f, setF] = useState({ displayName: me.user.name, bio: "", phone: me.user.phone ?? "", doc: "cin" as "cin" | "passport" | "residence_card", langs: [locale] as string[] });
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const e = useErr();
  async function send(ev: FormEvent) {
    ev.preventDefault(); e.clear(); setBusy(true);
    try {
      if (f.phone !== (me.user.phone ?? "")) await api("/account/me", { method: "PATCH", body: { phone: f.phone } });
      await api("/account/host/apply", { body: { displayName: f.displayName, bio: f.bio || undefined, languages: f.langs } });
      if (file) await api("/account/host/id-verification", { body: { docType: f.doc, frontKey: await uploadImage("id", await shrinkImage(file)) } });
      done();
    } catch (x) { e.fail(x); } finally { setBusy(false); }
  }
  return (
    <form className="panel stack" onSubmit={send}>
      <h1 className="h2">{t("h.apply.title")}</h1>
      <p className="muted">{t("h.apply.lead")}</p>
      <label className="field"><span>{t("h.apply.name")}</span><input required minLength={2} value={f.displayName} onChange={(x) => setF({ ...f, displayName: x.target.value })} /></label>
      <label className="field"><span>{t("h.apply.phone")}</span><input required inputMode="numeric" pattern="\d{8,15}" dir="ltr" value={f.phone} onChange={(x) => setF({ ...f, phone: x.target.value.replace(/\D/g, "") })} /></label>
      <label className="field"><span>{t("h.apply.bio")}</span><textarea rows={3} value={f.bio} onChange={(x) => setF({ ...f, bio: x.target.value })} /></label>
      <fieldset className="field"><span>{t("h.apply.langs")}</span>
        <div className="row-inline">{(["fr", "ar", "en"] as const).map((l) => <label key={l} className="check"><input type="checkbox" checked={f.langs.includes(l)} onChange={(x) => setF({ ...f, langs: x.target.checked ? [...f.langs, l] : f.langs.filter((y) => y !== l) })} /> {l.toUpperCase()}</label>)}</div>
      </fieldset>
      <label className="field"><span>{t("h.apply.doc")}</span>
        <select value={f.doc} onChange={(x) => setF({ ...f, doc: x.target.value as typeof f.doc })}>{(["cin", "passport", "residence_card"] as const).map((d) => <option key={d} value={d}>{t(`h.apply.${d}`)}</option>)}</select>
      </label>
      <label className="attach"><input type="file" accept="image/jpeg,image/png,image/webp" required onChange={(x) => setFile(x.target.files?.[0] ?? null)} />{file ? file.name : t("h.apply.attach")}</label>
      {e.msg && <p className="err" role="alert">{e.msg}</p>}
      <button className="btn" type="submit" disabled={busy || !file || f.langs.length === 0}>{busy ? t("req.sending") : t("h.apply.send")}</button>
    </form>
  );
}

function Dashboard({ me }: { me: Me }) {
  const { t, locale } = useI18n();
  const [mine, setMine] = useState<Mine[] | null>(null);
  const [stays, setStays] = useState<Stay[] | null>(null);
  useEffect(() => { api<Mine[]>("/host/listings").then(setMine).catch(() => setMine([])); api<Stay[]>("/host/bookings").then(setStays).catch(() => setStays([])); }, []);
  const due = (stays ?? []).filter((s) => s.payout?.status === "pending").reduce((n, s) => n + s.payout!.amountMinor, 0);
  const cur = stays?.[0]?.currency ?? "MAD";
  return (
    <div className="stack" style={{ maxWidth: 760 }}>
      <p className="muted small">{t("h.sekaniNote")}</p>
      <section>
        <div className="section-head"><h2 className="h2">{t("h.listings")}</h2>{me.host?.status === "verified" && <Link className="btn" to="/host/l/new">{t("h.new")}</Link>}</div>
        {mine?.length === 0 && <p className="muted">{t("h.none")}</p>}
        {mine?.map((l) => <Link key={l.id} to={`/host/l/${l.id}`} className="kv"><span>{pick(l.title, locale)}</span><span className="muted small">{t(`l.${l.status}`)} · <span className="num">{money(l.basePriceMinor, l.currency, locale)}</span></span></Link>)}
      </section>
      <section>
        <div className="section-head"><h2 className="h2">{t("h.payouts")}</h2><b className="num" style={{ fontWeight: 300 }}>{money(due, cur, locale)}</b></div>
        {stays?.length === 0 && <p className="muted">{t("h.nobookings")}</p>}
        {stays?.map((s) => (
          <div key={s.id} className="kv"><span><span className="num muted small">{s.code}</span> {s.listingTitle ? pick(s.listingTitle, locale) : ""}<br /><span className="num small" dir="ltr">{s.checkIn} → {s.checkOut}</span></span>
            <span className="small" style={{ textAlign: "end" }}>{t(`status.${s.status}`)}{s.payout && <><br /><b className="num" style={{ fontWeight: 400 }}>{money(s.payout.amountMinor, s.currency, locale)}</b> <span className="muted">{s.payout.status === "paid" ? t("h.payout.paid") : t("h.payout.due")}</span></>}</span></div>
        ))}
      </section>
    </div>
  );
}

const BLANK = { cityId: 0, titleFr: "", titleAr: "", descFr: "", propertyType: "apartment", address: "", lat: "", lng: "", maxGuests: 2, bedrooms: 1, beds: 1, bathrooms: 1, price: "", cleaning: "0" };

export function HostListing() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, locale } = useI18n();
  const [vocab, setVocab] = useState<Vocab | null>(null);
  const [l, setL] = useState<Full | null>(null);
  const [f, setF] = useState(BLANK);
  const [note, setNote] = useState<string | null>(null);
  const e = useErr();
  const set = <K extends keyof typeof BLANK>(k: K, v: (typeof BLANK)[K]) => setF((p) => ({ ...p, [k]: v }));

  const load = useCallback(async () => {
    if (!id) return;
    const x = await api<Full>(`/host/listings/${id}`);
    setL(x);
    setF({ cityId: x.cityId, titleFr: x.title.fr, titleAr: x.title.ar ?? "", descFr: x.description.fr, propertyType: x.propertyType, address: x.address, lat: x.lat == null ? "" : String(x.lat), lng: x.lng == null ? "" : String(x.lng), maxGuests: x.maxGuests, bedrooms: x.bedrooms, beds: x.beds, bathrooms: Number(x.bathrooms), price: String(x.basePriceMinor / 100), cleaning: String(x.cleaningFeeMinor / 100) });
  }, [id]);
  useEffect(() => { api<Vocab>("/vocab").then((v) => { setVocab(v); setF((p) => (p.cityId ? p : { ...p, cityId: v.cities[0]?.id ?? 0 })); }); }, []);
  useEffect(() => { void load(); }, [load]);

  const body = () => ({
    cityId: f.cityId, title: { fr: f.titleFr.trim(), ...(f.titleAr.trim() ? { ar: f.titleAr.trim() } : {}) }, description: { fr: f.descFr.trim() }, propertyType: f.propertyType, roomType: "entire_place",
    address: f.address.trim(), lat: f.lat.trim() === "" ? null : Number(f.lat), lng: f.lng.trim() === "" ? null : Number(f.lng), maxGuests: f.maxGuests, bedrooms: f.bedrooms, beds: f.beds, bathrooms: f.bathrooms, basePriceMinor: toMinor(f.price), cleaningFeeMinor: toMinor(f.cleaning || "0"),
  });
  async function save(ev: FormEvent) {
    ev.preventDefault(); e.clear(); setNote(null);
    try {
      if (!id) { const row = await api<{ id: string }>("/host/listings", { body: body() }); nav(`/host/l/${row.id}`, { replace: true }); }
      else { await api(`/host/listings/${id}`, { method: "PATCH", body: body() }); setNote(t("e.saved")); await load(); }
    } catch (x) { e.fail(x); }
  }
  async function photos(files: FileList | null) {
    if (!files || !id) return;
    e.clear();
    try { for (const file of Array.from(files)) await api(`/host/listings/${id}/photos`, { body: { key: await uploadImage("listing", await shrinkImage(file)) } }); await load(); } catch (x) { e.fail(x); }
  }
  const valid = f.titleFr.trim().length > 1 && f.descFr.trim().length > 1 && f.address.trim().length > 2 && f.price !== "" && ((f.lat.trim() === "") === (f.lng.trim() === "")) && !Number.isNaN(Number(f.lat || 0)) && !Number.isNaN(Number(f.lng || 0)) && f.cityId > 0;
  return (
    <div className="wrap section">
      <Link to="/host" className="link small">{t("e.back")}</Link>
      <h1 className="h1" style={{ margin: "16px 0 32px" }}>{l ? pick(l.title, locale) : t("h.new")}</h1>
      <div className="stack">
        <form className="stack" onSubmit={save}>
          <label className="field"><span>{t("e.city")}</span><select value={f.cityId} onChange={(x) => set("cityId", Number(x.target.value))}>{vocab?.cities.map((c) => <option key={c.id} value={c.id}>{pick(c.name, locale)}</option>)}</select></label>
          <label className="field"><span>{t("e.titleFr")}</span><input value={f.titleFr} onChange={(x) => set("titleFr", x.target.value)} /></label>
          <label className="field"><span>{t("e.titleAr")}</span><input dir="rtl" value={f.titleAr} onChange={(x) => set("titleAr", x.target.value)} /></label>
          <label className="field"><span>{t("e.descFr")}</span><textarea rows={5} value={f.descFr} onChange={(x) => set("descFr", x.target.value)} /></label>
          <label className="field"><span>{t("e.type")}</span><select value={f.propertyType} onChange={(x) => set("propertyType", x.target.value)}>{PROPERTY_TYPES.map((p) => <option key={p} value={p}>{t(`p.${p}`)}</option>)}</select></label>
          <label className="field"><span>{t("e.address")}</span><input value={f.address} onChange={(x) => set("address", x.target.value)} /></label>
          <div className="row-inline">
            <label className="field"><span>{t("e.lat")}</span><input inputMode="decimal" dir="ltr" value={f.lat} onChange={(x) => set("lat", x.target.value)} /></label>
            <label className="field"><span>{t("e.lng")}</span><input inputMode="decimal" dir="ltr" value={f.lng} onChange={(x) => set("lng", x.target.value)} /></label>
          </div>
          <button type="button" className="link small" onClick={() => navigator.geolocation?.getCurrentPosition((p) => { set("lat", p.coords.latitude.toFixed(5)); set("lng", p.coords.longitude.toFixed(5)); })}>{t("e.here")}</button>
          <div className="row-inline">
            {([["maxGuests", "e.guests", 1], ["bedrooms", "e.bedrooms", 0], ["beds", "e.beds", 1], ["bathrooms", "e.baths", 0.5]] as const).map(([k, label, min]) => (
              <label key={k} className="field"><span>{t(label)}</span><input type="number" min={min} step={k === "bathrooms" ? 0.5 : 1} value={f[k]} onChange={(x) => set(k, Number(x.target.value))} /></label>
            ))}
          </div>
          <div className="row-inline">
            <label className="field"><span>{t("e.price")}</span><input inputMode="decimal" dir="ltr" value={f.price} onChange={(x) => set("price", x.target.value)} /></label>
            <label className="field"><span>{t("e.cleaning")}</span><input inputMode="decimal" dir="ltr" value={f.cleaning} onChange={(x) => set("cleaning", x.target.value)} /></label>
          </div>
          {e.msg && <p className="err" role="alert">{e.msg}</p>}
          <button className="btn" type="submit" disabled={!valid}>{id ? t("e.save") : t("e.create")}</button>
          {note && <p className="muted small" role="status">{note}</p>}
        </form>

        {l && (
          <>
            <section className="stack">
              <h2 className="h2">{t("e.photos")} ({l.photos.length})</h2>
              <p className="muted small">{t("e.photosHint")}</p>
              <div className="thumbs">{l.photos.map((p) => (
                <div key={p.id}><img src={mediaUrl(p.key)} alt="" loading="lazy" /><button type="button" className="link small" onClick={async () => { await api(`/host/listings/${l.id}/photos/${p.id}`, { method: "DELETE" }); await load(); }}>{t("e.remove")}</button></div>
              ))}</div>
              <label className="attach"><input type="file" multiple accept="image/jpeg,image/png,image/webp" onChange={(x) => { void photos(x.target.files); x.target.value = ""; }} />{t("e.addPhotos")}</label>
            </section>
            <Blocks id={l.id} />
            {l.status === "draft" && <button className="btn ghost" onClick={async () => { e.clear(); try { await api(`/host/listings/${l.id}/submit`, { body: {} }); setNote(t("e.submitted")); await load(); } catch (x) { e.fail(x); } }}>{t("e.submit")}</button>}
          </>
        )}
      </div>
    </div>
  );
}

interface Hold { id: string; kind: string; startDate: string; endDate: string }
function Blocks({ id }: { id: string }) {
  const { t } = useI18n();
  const [holds, setHolds] = useState<Hold[]>([]);
  const [a, setA] = useState(""); const [b, setB] = useState("");
  const e = useErr();
  const load = useCallback(async () => {
    const from = new Date().toISOString().slice(0, 10);
    setHolds(await api<Hold[]>(`/host/listings/${id}/calendar?from=${from}&to=${new Date(Date.now() + 400 * 864e5).toISOString().slice(0, 10)}`).catch(() => []));
  }, [id]);
  useEffect(() => { void load(); }, [load]);
  return (
    <section className="stack">
      <h2 className="h2">{t("e.calendar")}</h2>
      <p className="muted small">{t("e.calHint")}</p>
      {holds.map((h) => <div key={h.id} className="kv"><span className="num" dir="ltr">{h.startDate} → {h.endDate}</span>{h.kind === "host_block" ? <button className="link small" onClick={async () => { await api(`/host/listings/${id}/blocks/${h.id}`, { method: "DELETE" }); await load(); }}>{t("e.remove")}</button> : <span className="muted small">{t(`status.${h.kind === "booking" ? "confirmed" : "requested"}`)}</span>}</div>)}
      <div className="row-inline"><label className="field"><span>{t("e.from")}</span><input type="date" value={a} onChange={(x) => setA(x.target.value)} /></label><label className="field"><span>{t("e.to")}</span><input type="date" value={b} onChange={(x) => setB(x.target.value)} /></label></div>
      {e.msg && <p className="err" role="alert">{e.msg}</p>}
      <button className="btn ghost" disabled={!a || !b || b <= a} onClick={async () => { e.clear(); try { await api(`/host/listings/${id}/blocks`, { body: { startDate: a, endDate: b } }); setA(""); setB(""); await load(); } catch (x) { e.fail(x); } }}>{t("e.block")}</button>
    </section>
  );
}

export function HostPage() {
  const { t } = useI18n();
  const { me, refresh, signOut } = useAuth();
  return (
    <div className="wrap section">
      <div className="section-head"><h1 className="h1">{t("h.title")}</h1>{me && <button className="link small" onClick={() => void signOut()}>{t("h.signout")}</button>}</div>
      {me === null && <div className="stack"><AuthForm why={t("a.why")} /></div>}
      {me && !me.host && <div className="stack"><Apply me={me} done={refresh} /></div>}
      {me?.host?.status === "pending" && <><p className="panel">{t("h.pending")}</p><Dashboard me={me} /></>}
      {me?.host?.status === "suspended" && <p className="panel">{t("h.suspended")}</p>}
      {me?.host?.status === "verified" && <Dashboard me={me} />}
    </div>
  );
}
